import { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Service Agreement page — replaces the old static /terms.
 * Renders the currently-published version in EN or ES based on language toggle.
 */
export default function ServiceAgreementPage() {
  const { language } = useLanguage();
  const es = language === 'es';
  const [agreement, setAgreement] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/service-agreement/current`)
      .then(r => setAgreement(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load agreement'));
  }, []);

  const content = agreement && (es ? agreement.content_es : agreement.content_en);
  const title = es ? 'Acuerdo de Servicio' : 'Service Agreement';
  const subtitle = es
    ? `Versión ${agreement?.version || ''} · Publicado el ${agreement ? new Date(agreement.published_at).toLocaleDateString('es-VE') : ''}`
    : `Version ${agreement?.version || ''} · Published ${agreement ? new Date(agreement.published_at).toLocaleDateString('en-US') : ''}`;

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="service-agreement-page">
      <div className="container mx-auto max-w-3xl py-12 px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 sm:p-12">
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">WakaCasa</span>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#0B192C] mb-2" data-testid="service-agreement-title">{title}</h1>
          {agreement && (
            <p className="text-xs text-slate-400 mb-8" data-testid="service-agreement-meta">{subtitle}</p>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="service-agreement-error">
              {error}
            </div>
          )}

          {!agreement && !error && (
            <div className="space-y-3">
              <Skeleton className="h-6 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          )}

          {content && (
            <article className="prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-[#0B192C] prose-h1:text-3xl prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-p:text-slate-600 prose-p:leading-relaxed prose-strong:text-[#0B192C] prose-a:text-[#D4AF37] prose-li:text-slate-600" data-testid="service-agreement-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </article>
          )}

          <div className="mt-10 pt-6 border-t border-slate-100 text-xs text-slate-400 flex justify-between flex-wrap gap-2">
            <Link to="/privacy" className="hover:text-[#D4AF37]" data-testid="agreement-to-privacy-link">
              {es ? '← Política de Privacidad' : '← Privacy Policy'}
            </Link>
            <span>© {new Date().getFullYear()} WakaCasa CA</span>
          </div>
        </div>
      </div>
    </div>
  );
}
